import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TrayCardViewModel } from '../hooks/useCaseCartDetailViewModel';
import { ASSET_STATUS_LABEL_KEYS } from '../utils/assetStatusLabels';
import { ReactComponent as TrayLocationIcon } from './icons/tray.svg';

const isOnCart = (card: TrayCardViewModel) => card.source !== 'pending';

// Height bumped from Figma's 190px to fit availabilityValueSx's 2-line wrap
// without clipping the rows below it.
const cardSx = (onCart: boolean) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  height: '222px',
  gap: '8px',
  overflow: 'hidden',
  borderRadius: '8px',
  background: onCart ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
  border: onCart ? 'none' : '1px dashed rgba(0, 0, 0, 0.5)',
});

const headerRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 16px 0 16px',
};

const availabilityLabelSx = {
  color: '#64748b',
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
};

// Wraps onto up to 2 lines instead of truncating to one (cardSx's height
// was bumped to fit this).
const availabilityValueSx = {
  color: '#000',
  fontSize: { xs: '19px', sm: '23px', md: '29px' },
  fontWeight: 700,
  letterSpacing: '-1.6px',
  lineHeight: 'normal',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical' as const,
  whiteSpace: 'normal' as const,
};

const availabilityTextGroupSx = {
  minWidth: 0,
  flex: 1,
};

const statusPillSx = (onCart: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '24px',
  borderRadius: '20px',
  padding: '5px 12px',
  backgroundColor: onCart ? '#901de4' : '#f2ff03',
  color: onCart ? '#fff' : '#000',
  fontSize: '11px',
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
});

// flex: 1 so this row absorbs the extra height cardSx adds beyond Figma's
// 190px, instead of it collapsing above the footer (locationRowSx, pinned
// via marginTop: auto).
const trayRowSx = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: '8px',
  padding: '8px 16px',
  borderTop: '1px solid #eff1f3',
};

const trayIdChipSx = {
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: '#f4f4f4',
  color: '#000',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
  whiteSpace: 'nowrap' as const,
};

const trayNameSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const locationRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '16px',
  borderTop: '1px solid #eff1f3',
  marginTop: 'auto',
};

const locationInfoSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  minWidth: 0,
};

const locationIconContainerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: '#f4f4f4',
  flexShrink: 0,
};

// Figma renders this icon at its natural (non-square) size, rotated 30.02deg.
const locationIconSx = {
  color: 'rgba(0, 0, 0, 0.54)',
  width: '21.2428px',
  height: '19.9151px',
  transform: 'rotate(30.02deg)',
};

const locationLabelSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
};

const locationValueSx = (isUnavailable: boolean) => ({
  color: isUnavailable ? '#e53284' : '#000',
  fontSize: '16px',
  letterSpacing: '0.15px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

const locationValuePrimarySx = { fontWeight: 700 };
const locationValueSecondarySx = { fontWeight: 400 };

// Tappable area expanded to the ticket's 48x48dp tap-target requirement;
// the visible pill (removePillSx) stays Figma's 32px tall.
const removeHitAreaSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '48px',
  minHeight: '48px',
  cursor: 'pointer',
  flexShrink: 0,
};

const removePillSx = {
  display: 'flex',
  alignItems: 'center',
  height: '32px',
  padding: '8px 16px',
  border: '1px solid #e53284',
  borderRadius: '4px',
};

const removeLabelSx = {
  color: '#e53284',
  fontSize: '11.6px',
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
};

type TrayDetailCardProps = {
  card: TrayCardViewModel;
  onRemove: (card: TrayCardViewModel) => void;
  /** True once the case's cart has been dispatched — nothing can be removed from it anymore. */
  removeDisabled?: boolean;
};

export const TrayDetailCard = ({ card, onRemove, removeDisabled = false }: TrayDetailCardProps) => {
  const { t } = useTranslation(['periverse']);
  const onCart = isOnCart(card);
  const canRemove = onCart && !removeDisabled;
  const isUnavailable = !onCart && !card.availability;

  const availabilityLabel = card.status ? t(ASSET_STATUS_LABEL_KEYS[card.status]) : '—';

  // priorityAssetLocationDisplay is the location of the specific unit behind
  // availabilityLabel above. The picking station is never shown as a
  // fallback here — it isn't the tray's own location, so showing it would
  // be misleading; "No Location - Unavailable" is shown instead.
  const locationValue = onCart
    ? card.locationDisplay ?? '—'
    : card.availability
      ? t('pages.buildCart.progress.availableLabel', {
          location: card.availability.locationName,
          count: card.availability.count,
        })
      : t('pages.buildCart.progress.notAvailableAtLabel', {
          location:
            card.priorityAssetLocationDisplay ??
            t('pages.buildCart.progress.noLocationLabel'),
        });

  // "." separates a genuine location hierarchy (on-cart, e.g. "Cart 051 .
  // Station 1"); "-" stays for pending's location+availability pairing
  // (e.g. "Decon Wash - 2 Available"), which isn't a location segment.
  const [locationPrimarySegment, ...locationRestSegments] =
    locationValue.split(' - ');
  const locationSecondarySegment = locationRestSegments.join(' - ');
  const locationSeparator = onCart ? '.' : '-';
  const locationValueDisplay = locationSecondarySegment
    ? `${locationPrimarySegment} ${locationSeparator} ${locationSecondarySegment}`
    : locationPrimarySegment;

  return (
    <Box
      sx={cardSx(onCart)}
      data-testid="tray-detail-card"
      data-source={card.source}
    >
      <Box sx={headerRowSx}>
        <Box sx={availabilityTextGroupSx}>
          <Typography sx={availabilityLabelSx}>
            {t('pages.caseCartAssembly.progressScreen.availabilityLabel')}
          </Typography>
          <Tooltip title={availabilityLabel}>
            <Typography sx={availabilityValueSx} data-testid="tray-availability-value">
              {availabilityLabel}
            </Typography>
          </Tooltip>
        </Box>
        <Box sx={statusPillSx(onCart)} data-testid="tray-status-pill">
          {onCart
            ? t('pages.caseCartAssembly.progressScreen.onCartStatusLabel')
            : t('pages.caseCartAssembly.progressScreen.pendingStatusLabel')}
        </Box>
      </Box>

      <Box sx={trayRowSx}>
        <Box component="span" sx={trayIdChipSx}>
          {card.productNumber}
        </Box>
        <Tooltip title={card.name}>
          <Typography sx={trayNameSx}>{card.name}</Typography>
        </Tooltip>
      </Box>

      <Box sx={locationRowSx}>
        <Box sx={locationInfoSx}>
          <Box sx={locationIconContainerSx}>
            <TrayLocationIcon style={locationIconSx} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={locationLabelSx}>
              {t('pages.caseCartAssembly.progressScreen.trayLabel')}
            </Typography>
            <Tooltip title={locationValueDisplay}>
              <Typography
                sx={locationValueSx(isUnavailable)}
                data-testid="tray-location-value"
              >
                {locationPrimarySegment ? (
                  <>
                    <Box component="span" sx={locationValuePrimarySx}>
                      {locationPrimarySegment}
                    </Box>
                    {locationSecondarySegment && (
                      <Box component="span" sx={locationValueSecondarySx}>
                        {' '}
                        {locationSeparator} {locationSecondarySegment}
                      </Box>
                    )}
                  </>
                ) : (
                  locationValue
                )}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {canRemove && (
          <ButtonBase
            aria-label={t('pages.caseCartAssembly.progressScreen.removeTrayAriaLabel', {
              name: card.name,
            })}
            data-testid="tray-remove-button"
            onClick={() => onRemove(card)}
            sx={removeHitAreaSx}
          >
            <Box sx={removePillSx}>
              <Typography sx={removeLabelSx}>
                {t('pages.caseCartAssembly.progressScreen.removeLabel')}
              </Typography>
            </Box>
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};
